const DEFAULT_GITHUB_REPO = 'jd3655/signage';

export type GithubReleaseErrorCode =
  | 'network'
  | 'rate_limited'
  | 'not_found'
  | 'invalid_response'
  | 'no_img_xz_asset';

export class GithubReleaseError extends Error {
  code: GithubReleaseErrorCode;

  constructor(code: GithubReleaseErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

export interface LatestPiRelease {
  repoSlug: string;
  tag: string;
  assetName: string;
  assetUrl: string;
  setupSheetUrl: string;
}

interface GithubReleaseAsset {
  name?: unknown;
  browser_download_url?: unknown;
}

interface GithubReleaseResponse {
  tag_name?: unknown;
  assets?: unknown;
}

interface ResolveLatestPiReleaseOptions {
  repoSlug?: string;
  fetcher?: typeof fetch;
}

function readViteRepoOverride(): string | undefined {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const slug = env?.VITE_GITHUB_REPO?.trim();
  return slug || undefined;
}

export function resolveGithubRepoSlug(override?: string): string {
  const candidate = override?.trim() || readViteRepoOverride() || DEFAULT_GITHUB_REPO;
  const isValid = /^[^/\s]+\/[^/\s]+$/.test(candidate);
  return isValid ? candidate : DEFAULT_GITHUB_REPO;
}

export function buildPiSetupSheetUrl(repoSlug: string): string {
  return `https://github.com/${repoSlug}/blob/main/docs/RASPBERRY_PI.md`;
}

export function describeGithubReleaseError(code: GithubReleaseErrorCode): string {
  switch (code) {
    case 'rate_limited':
      return 'GitHub API rate limit reached. Try again in a few minutes.';
    case 'network':
      return 'Could not reach GitHub. Check your connection and retry.';
    case 'not_found':
      return 'Could not find releases for this repository.';
    case 'no_img_xz_asset':
      return 'Latest release has no .img.xz Raspberry Pi image.';
    case 'invalid_response':
    default:
      return 'GitHub returned an unexpected release response.';
  }
}

function parseLatestPiRelease(repoSlug: string, json: GithubReleaseResponse): LatestPiRelease {
  const tag = typeof json.tag_name === 'string' ? json.tag_name : '';
  const assets = Array.isArray(json.assets) ? (json.assets as GithubReleaseAsset[]) : null;
  if (!tag || !assets) {
    throw new GithubReleaseError('invalid_response');
  }

  const selected = assets.find((asset) => {
    return (
      typeof asset.name === 'string' &&
      asset.name.endsWith('.img.xz') &&
      typeof asset.browser_download_url === 'string' &&
      asset.browser_download_url.length > 0
    );
  });

  if (!selected || typeof selected.name !== 'string' || typeof selected.browser_download_url !== 'string') {
    throw new GithubReleaseError('no_img_xz_asset');
  }

  return {
    repoSlug,
    tag,
    assetName: selected.name,
    assetUrl: selected.browser_download_url,
    setupSheetUrl: buildPiSetupSheetUrl(repoSlug),
  };
}

export async function resolveLatestPiRelease(
  options: ResolveLatestPiReleaseOptions = {},
): Promise<LatestPiRelease> {
  const repoSlug = resolveGithubRepoSlug(options.repoSlug);
  const fetcher = options.fetcher ?? fetch;
  const url = `https://api.github.com/repos/${repoSlug}/releases/latest`;

  let response: Response;
  try {
    response = await fetcher(url, {
      headers: {
        Accept: 'application/vnd.github+json',
      },
    });
  } catch {
    throw new GithubReleaseError('network');
  }

  if (!response.ok) {
    if (response.status === 404) throw new GithubReleaseError('not_found');
    if (response.status === 429) throw new GithubReleaseError('rate_limited');
    if (response.status === 403 && response.headers.get('x-ratelimit-remaining') === '0') {
      throw new GithubReleaseError('rate_limited');
    }
    throw new GithubReleaseError('invalid_response');
  }

  let json: GithubReleaseResponse;
  try {
    json = (await response.json()) as GithubReleaseResponse;
  } catch {
    throw new GithubReleaseError('invalid_response');
  }

  return parseLatestPiRelease(repoSlug, json);
}
